import type { StorageItem } from "@src/domain/entities/storage/StorageItem";
import type { ISerializationPort, IStoragePort } from "@src/domain/ports";
import { StorageError } from "@src/infrastructure/errors/StorageError";

interface IStorageManager {
  get(key: string): string | null;
  set(key: string, value: string): void;
  delete(key: string): void;
  clear(): void;
}

type StorageAdapterDeleteErrorConfig = {
  errorMessage: (errorMessage: string) => string;
  unknownMessage: () => string;
  includeKeyOnError: boolean;
  includeKeyOnUnknown: boolean;
};

type StorageAdapterClearErrorConfig = {
  errorMessage: (errorMessage: string) => string;
  unknownMessage: () => string;
};

export const SESSION_STORAGE_DELETE_ERROR_CONFIG: StorageAdapterDeleteErrorConfig = {
  errorMessage: errorMessage => `Failed to delete data from sessionStorage: ${errorMessage}`,
  unknownMessage: () => "Failed to delete data from sessionStorage: Unknown error",
  includeKeyOnError: false,
  includeKeyOnUnknown: false,
};

export const SESSION_STORAGE_CLEAR_ERROR_CONFIG: StorageAdapterClearErrorConfig = {
  errorMessage: errorMessage => `Failed to clear sessionStorage: ${errorMessage}`,
  unknownMessage: () => "Failed to clear sessionStorage: Unknown error",
};

export const LOCAL_STORAGE_DELETE_ERROR_CONFIG: StorageAdapterDeleteErrorConfig = {
  errorMessage: errorMessage => `Failed to delete item: ${errorMessage}`,
  unknownMessage: () => "Failed to delete item",
  includeKeyOnError: true,
  includeKeyOnUnknown: true,
};

export const LOCAL_STORAGE_CLEAR_ERROR_CONFIG: StorageAdapterClearErrorConfig = {
  errorMessage: errorMessage => `Failed to clear storage: ${errorMessage}`,
  unknownMessage: () => "Failed to clear storage",
};

export abstract class StorageAdapterBase implements IStoragePort {
  private readonly storageManager: IStorageManager;
  private readonly serializer: ISerializationPort;
  private readonly deleteErrorConfig: StorageAdapterDeleteErrorConfig;
  private readonly clearErrorConfig: StorageAdapterClearErrorConfig;

  protected constructor(
    storageManager: IStorageManager,
    serializer: ISerializationPort,
    deleteErrorConfig: StorageAdapterDeleteErrorConfig,
    clearErrorConfig: StorageAdapterClearErrorConfig
  ) {
    this.storageManager = storageManager;
    this.serializer = serializer;
    this.deleteErrorConfig = deleteErrorConfig;
    this.clearErrorConfig = clearErrorConfig;
  }

  async retrieve<T>(key: string, mustCheckExpiration?: boolean): Promise<StorageItem<T> | null> {
    try {
      const data = this.storageManager.get(key);
      if (!data) return null;

      const localItem = this.serializer.deserialize<StorageItem<T>>(data);
      const dataExpirationTime = localItem?.expirationTimeStamp;

      if (
        (dataExpirationTime && dataExpirationTime < Date.now()) ||
        (mustCheckExpiration && !dataExpirationTime)
      ) {
        this.storageManager.delete(key);
        return null;
      }

      return localItem;
    } catch (retrieveError: unknown) {
      if (retrieveError instanceof Error) {
        throw new StorageError(`Failed to retrieve item: ${retrieveError.message}`, key);
      }
      throw new StorageError("Failed to retrieve item", key);
    }
  }

  async store<T>(input: Pick<StorageItem<T>, "key" | "value">, expirationDuration?: number): Promise<void> {
    try {
      const item: StorageItem<T> = {
        ...input,
        timestamp: Date.now(),
      };

      if (expirationDuration) {
        const expirationDurationMilliseconds = expirationDuration * 1000;
        item.expirationTimeStamp = item.timestamp + expirationDurationMilliseconds;
      }

      const serialized = this.serializer.serialize(item);
      this.storageManager.set(item.key, serialized);
    } catch (storeError: unknown) {
      if (storeError instanceof Error) {
        throw new StorageError(`Failed to store item: ${storeError.message}`, input.key);
      }
      throw new StorageError("Failed to store item", input.key);
    }
  }

  async delete(key: string): Promise<void> {
    try {
      this.storageManager.delete(key);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new StorageError(
          this.deleteErrorConfig.errorMessage(error.message),
          this.deleteErrorConfig.includeKeyOnError ? key : null
        );
      }

      throw new StorageError(
        this.deleteErrorConfig.unknownMessage(),
        this.deleteErrorConfig.includeKeyOnUnknown ? key : null
      );
    }
  }

  async clear(): Promise<void> {
    try {
      this.storageManager.clear();
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new StorageError(this.clearErrorConfig.errorMessage(error.message));
      }
      throw new StorageError(this.clearErrorConfig.unknownMessage());
    }
  }
}

